import { useTranslation } from 'react-i18next';
import { motion } from 'motion/react';
import { ReactNode, useCallback, type HTMLAttributes } from 'react';
import { color } from '@/colors';
import { I18N_KEYS } from '@/language/keys';
import { FlexCenVer } from '@/UI/styles';
import { ImageIcon } from '@/UI/Icons/ImageIcon';
import { ImageSlashIcon } from '@/UI/Icons/ImageSlashIcon';
import { VideoIcon } from '@/UI/Icons/VideoIcon';
import { VideoSlashIcon } from '@/UI/Icons/VideoSlashIcon';
import { TextIcon } from '@/UI/Icons/TextIcon';
import { TextSlashIcon } from '@/UI/Icons/TextSlashIcon';
import { AudioIcon } from '@/UI/Icons/AudioIcon';
import { AudioSlashIcon } from '@/UI/Icons/AudioSlashIcon';
import { useCompositorView, useFlowView } from '@/components/Recipe/FlowContext';
import { useIsHovered } from '@/hooks/useIsHovered';
import { TextSmRg } from '@/UI/AppTypography/AppTypography';
import {
  CanvasIcon,
  EyeIcon,
  EyeClosedIcon,
  LockClosedIcon,
  LockOpenIcon,
  SpeakerHighIcon,
  SpeakerXIcon,
} from '@/UI/Icons';
import { AppIconButton } from '@/UI/Buttons/AppIconButton';
import type { Layer, LayerId, NodeId } from 'web';

const BaseLayerRow = ({ children, ...props }: { children?: ReactNode } & HTMLAttributes<HTMLDivElement>) => {
  return (
    <FlexCenVer
      sx={{
        color: color.White80_T,
        flexGrow: 1,
        justifyContent: 'space-between',
        p: 0.5,
        width: '100%',
      }}
      {...props}
    >
      {children}
    </FlexCenVer>
  );
};

const LayerIconAndName = ({ children }: { children: ReactNode }) => (
  <FlexCenVer sx={{ gap: 0.5, flexGrow: 1 }}>{children}</FlexCenVer>
);

const layerRowColors = (isSelected: boolean, isHovered: boolean) => {
  const isEmphasized = isSelected || isHovered;
  return {
    iconColor: isEmphasized ? color.White100 : color.White64_T,
    textColor: isEmphasized ? color.White100 : color.White80_T,
  };
};

const LayerTypeIcon = ({ layer, hasInput, iconColor }: { layer: Layer; hasInput: boolean; iconColor: string }) => {
  const iconStyle = { width: 16, height: 16, color: iconColor };

  if (hasInput) {
    if (layer.kind.type === 'image') return <ImageIcon style={iconStyle} />;
    if (layer.kind.type === 'video') return <VideoIcon style={iconStyle} />;
    if (layer.kind.type === 'audio') return <AudioIcon style={iconStyle} />;
    return <TextIcon style={iconStyle} />;
  }

  if (layer.kind.type === 'image') return <ImageSlashIcon style={iconStyle} />;
  if (layer.kind.type === 'video') return <VideoSlashIcon style={iconStyle} />;
  if (layer.kind.type === 'audio') return <AudioSlashIcon style={iconStyle} />;
  return <TextSlashIcon style={iconStyle} />;
};

export const CanvasLayerRow = ({ isSelected }: { isSelected: boolean }) => {
  const { isHovered, ...divProps } = useIsHovered();
  const { t } = useTranslation();
  const { iconColor, textColor } = layerRowColors(isSelected, isHovered);

  return (
    <BaseLayerRow {...divProps}>
      <LayerIconAndName>
        <CanvasIcon style={{ width: 16, height: 16, color: iconColor }} />
        <TextSmRg text={t(I18N_KEYS.COMPOSITOR.LAYER_PANEL_ROW.CANVAS_LAYER_NAME)} color={textColor} />
      </LayerIconAndName>
    </BaseLayerRow>
  );
};

export const LayerRow = ({
  layer,
  layerId,
  nodeId,
  isSelected,
}: {
  layer: Layer;
  layerId: LayerId;
  nodeId: NodeId;
  isSelected: boolean;
}) => {
  const { isHovered, ...divProps } = useIsHovered();
  const flowView = useFlowView();

  const hasInput = flowView.nodeInputType(nodeId, layer.kind.inputId) !== undefined;
  const { iconColor, textColor } = layerRowColors(isSelected, isHovered);

  return (
    <BaseLayerRow {...divProps}>
      <LayerIconAndName>
        <LayerTypeIcon layer={layer} hasInput={hasInput} iconColor={iconColor} />
        <TextSmRg text={layer.name} color={textColor} />
      </LayerIconAndName>
      <FlexCenVer sx={{ gap: 0.25 }}>
        <LayerMute layer={layer} layerId={layerId} nodeId={nodeId} isHovered={isHovered} />
        <LayerVisibility layer={layer} layerId={layerId} nodeId={nodeId} isHovered={isHovered} />
        <LayerLock layer={layer} layerId={layerId} nodeId={nodeId} isHovered={isHovered} />
      </FlexCenVer>
    </BaseLayerRow>
  );
};

const LayerRowAction = ({
  isHovered,
  isActive,
  children,
}: {
  isHovered: boolean;
  isActive: boolean;
  children: ReactNode;
}) => {
  return (
    <motion.div
      initial={{ opacity: isActive ? 1 : 0 }}
      animate={{ opacity: isHovered || isActive ? 1 : 0 }}
      transition={{ duration: 0.2 }}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {children}
    </motion.div>
  );
};

const LayerMute = ({
  isHovered,
  layer,
  layerId,
  nodeId,
}: {
  isHovered: boolean;
  layer: Layer;
  layerId: LayerId;
  nodeId: NodeId;
}) => {
  const compositorView = useCompositorView(nodeId);

  if (layer.kind.type !== 'video' && layer.kind.type !== 'audio') return null;

  const isMuted = layer.kind.muted;

  return (
    <LayerRowAction isHovered={isHovered} isActive={isMuted}>
      <AppIconButton
        mode="on-light"
        width={20}
        height={20}
        onClick={() => compositorView.setLayerMuted(layerId, !isMuted)}
      >
        {isMuted ? (
          <SpeakerXIcon style={{ width: 16, height: 16 }} />
        ) : (
          <SpeakerHighIcon style={{ width: 16, height: 16 }} />
        )}
      </AppIconButton>
    </LayerRowAction>
  );
};

const LayerVisibility = ({
  isHovered,
  layer,
  layerId,
  nodeId,
}: {
  isHovered: boolean;
  layer: Layer;
  layerId: LayerId;
  nodeId: NodeId;
}) => {
  const compositorView = useCompositorView(nodeId);

  if (layer.kind.type === 'audio') return null;

  const isHidden = !layer.visible;

  return (
    <LayerRowAction isHovered={isHovered} isActive={isHidden}>
      <AppIconButton
        mode="on-light"
        width={20}
        height={20}
        onClick={() => compositorView.setLayerVisible(layerId, !layer.visible)}
      >
        {isHidden ? <EyeClosedIcon style={{ width: 16, height: 16 }} /> : <EyeIcon style={{ width: 16, height: 16 }} />}
      </AppIconButton>
    </LayerRowAction>
  );
};

const LayerLock = ({
  isHovered,
  layer,
  layerId,
  nodeId,
}: {
  isHovered: boolean;
  layer: Layer;
  layerId: LayerId;
  nodeId: NodeId;
}) => {
  const compositorView = useCompositorView(nodeId);

  const isLocked = layer.locked;

  const toggleLock = useCallback(() => {
    compositorView.setIsLayerLocked(layerId, !isLocked);
  }, [compositorView, layerId, isLocked]);

  return (
    <LayerRowAction isHovered={isHovered} isActive={isLocked}>
      <AppIconButton mode="on-light" width={20} height={20} onClick={toggleLock}>
        {isLocked ? (
          <LockClosedIcon style={{ width: 16, height: 16 }} />
        ) : (
          <LockOpenIcon style={{ width: 16, height: 16 }} />
        )}
      </AppIconButton>
    </LayerRowAction>
  );
};
